// src/encounter/structured-note/jump-focus.ts
//
// PROD-2743 · frame v2. Where keyboard focus lands when a Sign-list name jumps to its blank item (audit
// FV2-22, WCAG 2.4.3): the item's own control, so a keyboard user carries on from the item and not from
// the top of the page. Used by the Sign list in structured-note-section.tsx.

/** Inside a jump target, the field for `label`: a control named by it, else the wrapper of the text that
 *  labels it (the renderer draws "<label> *" above its control). Null when nothing matches. */
function fieldNamed(target: HTMLElement, label: string): HTMLElement | null {
  const byName = [...target.querySelectorAll<HTMLElement>('[aria-label]')].find((el) => el.getAttribute('aria-label') === label);
  if (byName) return byName;
  const text = [...target.querySelectorAll<HTMLElement>('label, legend, span, p')].find(
    (el) => el.children.length === 0 && (el.textContent ?? '').replace(/\s*\*\s*$/, '').trim() === label,
  );
  return text?.parentElement ?? null;
}

/** The first control a keyboard can reach inside a jump target (or the target itself). */
const FOCUSABLE = 'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focus the item a Sign-list name jumps to: its own control (a run of several fields is searched for the
 *  field labelled with the item's name — Codex P2 on the 6 Oct fixes), else the target's first enabled
 *  control, else the target itself made programmatically focusable (tabIndex -1, never a new Tab stop).
 *  The scroll has already happened. */
export function focusJumpTarget(target: HTMLElement, label?: string): void {
  const named = label ? fieldNamed(target, label) : null;
  const scope = named ?? target;
  const control = [scope, ...scope.querySelectorAll<HTMLElement>(FOCUSABLE)].find((el) => el.matches(FOCUSABLE) && reachable(el));
  if (control) {
    control.focus({ preventScroll: true });
    // A control can refuse focus (Codex P2): then the target itself takes it, below.
    if (document.activeElement === control) return;
  }
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
}

/** Not hidden from everyone: no hidden input, nothing inside a `hidden`, `inert` or aria-hidden subtree. */
function reachable(el: HTMLElement): boolean {
  if (el instanceof HTMLInputElement && el.type === 'hidden') return false;
  return el.closest('[hidden], [inert], [aria-hidden="true"]') === null;
}


const scrollsY = (el: HTMLElement) => /^(auto|scroll|overlay)$/.test(getComputedStyle(el).overflowY);

/** True when some ancestor of `el` (below the document) is a vertical scroll container, overflowing or not. */
function hasScrollContainer(el: HTMLElement): boolean {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) if (scrollsY(p)) return true;
  return false;
}

/** The nearest ancestor of `el` that scrolls vertically and has something to scroll: the note's own scroll
 *  container. Null when none does. */
export function noteScrollerOf(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    if (scrollsY(p) && p.scrollHeight > p.clientHeight) return p;
  }
  return null;
}

/**
 * Bring a Sign-list jump target to the middle of the note's OWN scroll container, and move nothing else
 * (the independent review of #634, item A). `scrollIntoView({ block: 'center' })` scrolls EVERY scrollable
 * ancestor: the document shifted by 64px, the workspace tabs left the screen and a blank band showed at the
 * bottom. Only when no ancestor scrolls (the document is the note's scroller) does the document move, and
 * then only as far as needed (`block: 'nearest'`).
 */
export function scrollJumpTarget(target: HTMLElement): void {
  const scroller = noteScrollerOf(target);
  if (!scroller) {
    // The note has its own scroll container but nothing in it overflows: the target is already in it, and
    // the document stays put (Codex on e2e0eae). Only a note with no scroll container of its own — the
    // document is its scroller — moves the document, and only as far as needed.
    if (hasScrollContainer(target)) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return;
  }
  const t = target.getBoundingClientRect();
  const s = scroller.getBoundingClientRect();
  const top = Math.max(0, scroller.scrollTop + (t.top - s.top) - Math.max(0, (scroller.clientHeight - t.height) / 2));
  if (typeof scroller.scrollTo === 'function') scroller.scrollTo({ top, behavior: 'smooth' });
  else scroller.scrollTop = top;
}
